import React from 'react';
import { useIntl } from 'react-intl';
import { createPortal } from 'react-dom';
import { FocusTrap } from 'focus-trap-react';
import classNames, { Argument } from 'classnames';

import { AbstractMedia, MediaType } from 'types/AbstractMedia';

import { downloadURL } from 'utils/downloadUtils';

import Icon from '../Icon';
import Spinner from '../Spinner';
import StepIndicator from '../StepIndicator';
import { DEFAULT_MEDIA_COMPONENTS } from '../File';
import Button, { ButtonType } from '../Button';

export interface CarouselProps {
  className?: Argument;
  autoplay?: boolean;
  carouselClassName?: Argument;
  medias: Array<AbstractMedia>;
  error?: React.ReactNode;
  loading?: boolean;
  footer?: React.ReactElement<any>;
  onClose?: () => void;
  fullscreen?: boolean;
  hideActions?: boolean;
  hideCursor?: boolean;
  hideNavigationArrow?: boolean;
  showDownload?: boolean;
  defaultMediaIndex?: number;
  onDelete?: (media: AbstractMedia) => void;
  onMediaClick?: (media: AbstractMedia) => void;
  onMediaChange?: (newMediaIndex: number) => void;
  onPageChange?: (pageNumber: number, totalPages: number) => void;
  watermarkTitle?: string;
  compact?: boolean;
  aspectRatioClassName?: string;
}

const MIN_SWIPE_DISTANCE = 75;

const getCarouselStyle = (isFullscreen: boolean = false, aspectRatioClassName = 'aspect-video') => ({
  wrapper: isFullscreen
    ? 'fixed inset-0 h-dvh w-svw bg-default overflow-hidden z-60 flex flex-col'
    : 'relative w-full flex items-center',
  subWrapper: isFullscreen ? 'w-full grow overflow-y-auto' : 'mx-auto w-full',
  subSubWrapper: `${aspectRatioClassName} relative${isFullscreen ? ' flex size-full justify-center' : ''}`,
  media: `absolute ease-in duration-300 inset-0 ${isFullscreen ? 'flex items-center justify-center flex-col' : ''}`,
  arrow: `navigation flex items-center justify-center aspect-square rounded-button !p-2 absolute z-10 -mt-5 backdrop-blur-sm overflow-hidden duration-500 ${isFullscreen ? '!bg-black/70 hover:!bg-black/90 disabled:hover:!bg-black/70' : 'text-xl kiosk:text-2xl !bg-white/70 hover:!bg-white/90 disabled:hover:!bg-white/70 !text-default'} leading-none`,
});

const Carousel: React.FC<CarouselProps> = ({
  error,
  medias,
  footer,
  loading,
  onClose,
  compact,
  aspectRatioClassName = 'aspect-video',
  autoplay,
  onDelete,
  className,
  fullscreen,
  hideCursor,
  hideActions,
  onMediaClick,
  onPageChange,
  showDownload,
  onMediaChange,
  watermarkTitle,
  carouselClassName,
  defaultMediaIndex,
}) => {
  const intl = useIntl();
  const touchPositionRef = React.useRef<number>(null);

  const [currentIndex, setCurrentIndex] = React.useState(() => (defaultMediaIndex && defaultMediaIndex > -1 ? defaultMediaIndex : 0));

  React.useEffect(() => {
    if (defaultMediaIndex && defaultMediaIndex >= 0) {
      setCurrentIndex(defaultMediaIndex);
    }
  }, [defaultMediaIndex]);

  const onPrev = React.useCallback(() => {
    const newIndex = Math.max(0, currentIndex - 1);
    setCurrentIndex(newIndex);
    onMediaChange?.(newIndex);
  }, [currentIndex, onMediaChange]);

  const onNext = React.useCallback(() => {
    const newIndex = Math.min(medias.length - 1, currentIndex + 1);
    setCurrentIndex(newIndex);
    onMediaChange?.(newIndex);
  }, [currentIndex, medias.length, onMediaChange]);

  const handleTouchStart = React.useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    touchPositionRef.current = event.targetTouches[0].clientX;
  }, []);

  const handleTouchEnd = React.useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    if (touchPositionRef.current) {
      const x = event.changedTouches[0].clientX;
      if (Math.abs(x - touchPositionRef.current) > MIN_SWIPE_DISTANCE) {
        if (x < touchPositionRef.current) {
          onNext();
        } else if (x > touchPositionRef.current) {
          onPrev();
        }
      }
    }
  }, [onNext, onPrev]);

  const handleDelete = React.useCallback(() => {
    setCurrentIndex((index) => {
      onDelete?.(medias[index]);
      return index === medias.length - 1 ? Math.max(0, medias.length - 2) : index;
    });
  }, [onDelete, medias]);

  const handleDownload = React.useCallback(() => {
    const { path, filename } = medias[currentIndex];
    downloadURL(path, filename);
  }, [medias, currentIndex]);

  const handleMediaClick = React.useCallback(
    () => onMediaClick?.(medias[currentIndex]),
    [onMediaClick, medias, currentIndex],
  );

  const handleOnSeeking = React.useCallback(() => {
    touchPositionRef.current = null;
  }, []);

  const showActions = !hideActions && typeof onDelete === 'function';
  const displayFooter = fullscreen && (showActions || Boolean(footer));
  const showStepIndicator = !fullscreen && medias.length > 1;

  const styles = React.useMemo(() => getCarouselStyle(fullscreen, aspectRatioClassName), [fullscreen, aspectRatioClassName]);

  const displayCursor = React.useMemo(
    () => !hideCursor && !fullscreen && onMediaClick && medias.length > 1,
    [fullscreen, hideCursor, medias.length, onMediaClick],
  );

  const media = React.useMemo(() => medias[currentIndex], [currentIndex, medias]);

  // This allows the carousel to be rendered *over* a modal
  const portalFn = React.useMemo(
    () => (
      fullscreen
        ? (children: React.ReactNode) => createPortal(children, document.body)
        : (children: React.ReactNode) => children
    ),
    [fullscreen],
  );

  React.useEffect(() => {
    // Prevent to scroll in the page when you display a media in fullscreen
    const action = fullscreen ? 'add' : 'remove';
    document.body.classList[action]('overflow-hidden');

    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [fullscreen]);

  React.useEffect(() => {
    // Fallback
    const hasBeenRemoved = currentIndex > 0 && currentIndex >= medias.length;
    if (hasBeenRemoved) {
      setCurrentIndex(medias.length - 1);
    }
  }, [setCurrentIndex, currentIndex, medias.length]);

  const displayError = React.useMemo(() => !loading && Boolean(error), [error, loading]);
  const displayMedia = React.useMemo(() => !loading && !error && medias.length > 0 && media, [error, loading, media, medias.length]);

  const wrapperProps = React.useMemo(() => fullscreen ? { role:'dialog', 'aria-modal': 'true' } : {}, [fullscreen]) as React.HTMLAttributes<HTMLDivElement>;

  return portalFn(
    <FocusTrap active={!!fullscreen} paused={!fullscreen} focusTrapOptions={{ allowOutsideClick: true }}>
      <div className={classNames(styles.wrapper, 'carousel', carouselClassName, { multiMedia: medias.length > 1, compact })} {...wrapperProps}>
        {fullscreen && media && (
          <div className="flex justify-between gap-2 z-10 px-4 kiosk:px-10 kioskSharebox:px-8 left-0 shrink-0 h-20 kiosk:h-36 kioskSharebox:h-28 bg-default w-full text-white items-center backdrop-blur-sm">
            <div className="size-7 kiosk:size-14 kioskSharebox:size-10">
              {showDownload && (
                <Button
                  type={ButtonType.ICON}
                  onClick={handleDownload}
                  testId="download-media-carousel"
                  className="!p-0"
                  aria-label={intl.formatMessage({ id: 'accessibility.button.downloadMedia', defaultMessage: 'Download the media' })}>
                  <Icon className="fb-icon--download text-2xl kiosk:text-5xl" />
                </Button>
              )}
            </div>
            {media.type === MediaType.DOCUMENT && (
              <span
                data-testid="filename-carousel"
                className="truncate text-center flex-grow font-bold text-base kiosk:text-3xl kioskSharebox:text-2xl !text-inverse"
              >
                {media.filename}
              </span>
            )}
            <Button
              type={ButtonType.ICON}
              className="size-7 kiosk:size-14 kioskSharebox:size-10 !p-0"
              onClick={onClose}
              testId="close-carousel"
              aria-label={intl.formatMessage({ id: 'close', defaultMessage: 'Close' })}
            >
              <Icon className="fb-icon--close text-2xl kiosk:text-5xl" />
            </Button>
          </div>
        )}
        <div
          data-testid="carousel"
          onTouchEnd={handleTouchEnd}
          className={styles.subWrapper}
          onTouchStart={handleTouchStart}
        >
          <div className={styles.subSubWrapper}>
            <div
              onClick={handleMediaClick}
              data-testid="carousel-inner"
              className={classNames(
                `relative overflow-hidden ${aspectRatioClassName} scroll-smooth z-0 w-full flex items-center justify-center`,
                { 'cursor-pointer': displayCursor },
                className,
              )}
            >
              {loading && <Spinner />}
              {displayError && error}
              {displayMedia && (
                <div
                  key={media.uploadId ?? media.path}
                  className={styles.media}
                  tabIndex={0}// Needed to have at least 1 element tabbable in case if there is no navigation button
                >
                  {DEFAULT_MEDIA_COMPONENTS[media.type]?.(media, {
                    onPageChange,
                    watermarkTitle,
                    thumbnail: !fullscreen,
                    onSeeking: handleOnSeeking,
                    autoplay: fullscreen || (autoplay && currentIndex === 0),
                  })}
                </div>
              )}
            </div>
            {medias.length > 1 && (
              <>
                <Button
                  onClick={onPrev}
                  type={ButtonType.ICON}
                  className={classNames(styles.arrow, 'carousel-leftArrow')}
                  testId="carousel-leftArrow"
                  aria-label={intl.formatMessage({ id: 'accessibility.button.previous', defaultMessage: 'Previous' })}
                  disabled={currentIndex === 0}>
                  <Icon className="fb-icon--arrow-left text-2xl kiosk:text-4xl" />
                </Button>
                <Button
                  onClick={onNext}
                  type={ButtonType.ICON}
                  className={classNames(styles.arrow, 'carousel-rightArrow')}
                  testId="carousel-rightArrow"
                  aria-label={intl.formatMessage({ id: 'steps.next', defaultMessage: 'Next' })}
                  disabled={currentIndex === medias.length - 1}>
                  <Icon className="fb-icon--arrow-right text-2xl kiosk:text-4xl" />
                </Button>
              </>
            )}
          </div>
        </div>
        {displayFooter && (
          (<div className="px-4 flex justify-center items-center shrink-0 h-24 kiosk:h-44 kioskSharebox:h-40 w-full text-white bg-default backdrop-blur-sm">
            {onDelete && (
              <Button
                type={ButtonType.ICON}
                onClick={handleDelete}
                testId="delete-media-carousel"
                aria-label={intl.formatMessage({ id: 'accessibility.button.deleteMedia', defaultMessage: 'Delete the media' })}>
                <Icon className="fb-icon--delete text-2xl kiosk:text-5xl" />
              </Button>
            )}
            {footer}
          </div>)
        )}
        {showStepIndicator && (
          <StepIndicator
            length={medias.length}
            currentIndex={currentIndex}
            data-testid="steps-carousel"
            className="carousel-footer absolute bottom-0 py-4 bg-gradient-to-t from-slate-900/40 flex justify-center justify-items-center items-center w-full rounded-card"
          />
        )}
      </div>
    </FocusTrap>
  );
};

export default Carousel;
